import { useEffect, useEffectEvent, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { GRAND_BOUTON, TitreEcran } from "./commun";

type EtatDuScanner = "demarrage" | "actif" | "refuse" | "absent" | "non-charge";

type ChargerLeScanner = () => Promise<typeof import("qr-scanner")>;

const chargerQrScanner: ChargerLeScanner = () => import("qr-scanner");

/** L'acces a la camera a-t-il ete refuse (plutot qu'absent) ? */
export function estUnRefus(erreur: unknown): boolean {
  if (typeof DOMException !== "undefined" && erreur instanceof DOMException) {
    return erreur.name === "NotAllowedError" || erreur.name === "SecurityError";
  }
  const texte = erreur instanceof Error ? `${erreur.name} ${erreur.message}` : String(erreur);
  return /notallowed|denied|permission/i.test(texte);
}

/**
 * Le scanner dans la page : la camera arriere, et le premier QR code lu.
 *
 * qr-scanner (MIT) n'est charge qu'a l'ouverture du scanner : la page n'en paie pas le poids
 * tant qu'on tape les codes ou qu'on scanne avec l'appareil photo du telephone. Safari n'a pas
 * `BarcodeDetector` ; la bibliotheque s'en sert quand il existe et decode elle-meme sinon.
 * Elle ne touche pas au DOM hors de la video (aucun cadre ajoute par elle) : le viseur est
 * dessine ici.
 *
 * Charge a la demande veut dire charge par le reseau : sans connexion, ou si l'appli a ete mise
 * a jour depuis l'ouverture de la page (l'ancien fichier n'existe plus), le scanner ne vient
 * pas. Il le dit, et le code tape reste possible. `charger` : pour les tests.
 */
export function Scanner({
  onLu,
  onTaper,
  onFermer,
  charger = chargerQrScanner,
}: {
  onLu: (contenu: string) => void;
  onTaper: () => void;
  onFermer: () => void;
  charger?: ChargerLeScanner;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [etat, setEtat] = useState<EtatDuScanner>("demarrage");
  const lu = useEffectEvent((contenu: string) => onLu(contenu));

  useEffect(() => {
    const element = video.current;
    if (!element) return;
    let fini = false;
    let arreter: (() => void) | null = null;
    charger()
      .catch(() => {
        if (!fini) setEtat("non-charge");
        return null;
      })
      .then(async (module) => {
        if (!module || fini) return;
        const QrScanner = module.default;
        const scanner = new QrScanner(
          element,
          (resultat) => {
            if (fini) return;
            fini = true;
            scanner.stop();
            lu(resultat.data);
          },
          {
            preferredCamera: "environment",
            maxScansPerSecond: 8,
            returnDetailedScanResult: true,
          },
        );
        arreter = () => scanner.destroy();
        await scanner.start();
        if (!fini) setEtat("actif");
      })
      .catch((erreur: unknown) => {
        if (!fini) setEtat(estUnRefus(erreur) ? "refuse" : "absent");
      });
    return () => {
      fini = true;
      arreter?.();
    };
  }, [charger]);

  const indisponible = etat === "refuse" || etat === "absent" || etat === "non-charge";
  return (
    <section aria-labelledby="titre-scanner" className="flex flex-col gap-4">
      <TitreEcran id="titre-scanner" className="text-xl font-semibold">
        Scanner un choix
      </TitreEcran>
      <div className="relative overflow-hidden rounded-lg bg-black">
        <video
          ref={video}
          muted
          playsInline
          aria-label="Image de la caméra"
          className={`aspect-square w-full object-cover ${indisponible ? "invisible" : ""}`}
        />
        {etat === "actif" ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-[18%] rounded-lg border-4 border-white/80"
          />
        ) : null}
      </div>
      <p aria-live="polite" className="text-sm">
        {etat === "demarrage" ? "Ouverture de la caméra..." : null}
        {etat === "actif"
          ? "Posez le livre à plat et visez le QR code de votre choix, un seul à la fois."
          : null}
        {etat === "refuse"
          ? "L'accès à la caméra est refusé. Autorisez-le dans les réglages du navigateur, ou tapez le code écrit sous le QR code."
          : null}
        {etat === "absent"
          ? "Pas de caméra disponible. Tapez le code écrit sous le QR code."
          : null}
        {etat === "non-charge"
          ? "Le scanner n'a pas pu se charger. Vérifiez la connexion et rechargez la page, ou tapez le code écrit sous le QR code."
          : null}
      </p>
      <Button
        variant={indisponible ? "default" : "outline"}
        className={GRAND_BOUTON}
        onClick={onTaper}
      >
        Taper le code à la place
      </Button>
      <Button variant="ghost" onClick={onFermer}>
        Fermer le scanner
      </Button>
    </section>
  );
}
